<template>
  <div class="v2-video-categories">
    <h2 id="videos" class="mb-3 mb-md-4">
      {{ t("Fantasies on film") }}
    </h2>
    <div v-if="!tags.length" class="tags mb-3 mb-md-4">
      <tag-placeholder v-for="n in 14" :key="n" />
    </div>
    <div v-else-if="tags.length" class="tags mb-3 mb-md-4">
      <app-tag v-for="(tag, i) in tags" :key="i" v-modal.modal-signup :tag="tag" />
    </div>

    <div v-if="!videos.length" class="videos row">
      <div v-for="n in batchSize" :key="n" class="col-md-6 col-lg-4">
        <video-item-placeholder />
      </div>
    </div>
    <transition-group v-else name="fade" tag="div" class="videos row">
      <div v-for="video in shownVideos" :key="video.id" class="col-md-6 col-lg-4">
        <video-item :video="video" />
      </div>
    </transition-group>
    <div v-if="videos.length" class="d-flex justify-content-center mt-2">
      <button class="btn btn-outline-dark wide" @click="showMoreVideos">
        {{ t("See more") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, PropType, ref, watch } from "vue";
import { useModalStore } from "@/store/modal";
import AppTag from "@/components/v2/app-tag";
import { amplitudeButtonClickTrack } from "@/utils/amplitude";
import VideoItem from "@/components/v2/video-item/index.vue";
import VideoItemPlaceholder from "@/components/v2/placeholders/video-item-placeholder/index.vue";
import TagPlaceholder from "@/components/v2/placeholders/tag-placeholder/index.vue";
import { Tag } from "@/interfaces/Tags/Tag.ts";
import { Video } from "@/interfaces/Video/Video.ts";
import { useI18n } from "vue-i18n";
import { SFW_LANDING_IDS } from "@/utils/variables";

defineOptions({
  name: "video-categories",
});

const props = defineProps({
  tags: {
    type: Array as PropType<Tag[]>,
    default: () => [],
  },
  videos: {
    type: Array as PropType<Video[]>,
    default: () => [],
  },
  landingId: {
    type: Number as PropType<number | null>,
    default: null,
  },
});

const modalStore = useModalStore();
const { t } = useI18n();

const batchSize = computed(() => (props.landingId === SFW_LANDING_IDS.de ? 9 : 6));
const shownVideos = ref<Video[]>([]);

watch(
  () => props.videos,
  () => {
    shownVideos.value = props.videos.slice(0, batchSize.value);
  },
  { deep: true },
);

const trackClick = (name: string) => {
  amplitudeButtonClickTrack(name);
};

const showMoreVideos = () => {
  trackClick("Show more videos");
  const loadedCount = shownVideos.value.length;
  const limitReached = loadedCount >= props.videos.length || loadedCount >= batchSize.value * 3;

  if (limitReached) {
    modalStore.showModal({ id: "modal-signup" });
    return;
  }

  shownVideos.value = shownVideos.value.concat(props.videos.slice(loadedCount, loadedCount + batchSize.value));
};

onMounted(() => {
  shownVideos.value = props.videos ? props.videos.slice(0, batchSize.value) : [];
});
</script>

<style lang="scss">
@import "./index.scss";
</style>
